// Pure helpers for applying install-wide entity-level config (set in the hidden Advanced
// Settings app) to discovery results — display and export. Front-end only: this filters what
// users see and export, and never touches discovery/masking runtime.
//
// Config shape: { ENTITY_CODE: "HIDDEN" | "REPORT_ONLY" }. Absence === NORMAL.
//   HIDDEN      -> not shown in reports, excluded from export
//   REPORT_ONLY -> shown in reports, excluded from export
//   NORMAL      -> shown everywhere

export const LEVEL_NORMAL = 'NORMAL';
export const LEVEL_REPORT_ONLY = 'REPORT_ONLY';
export const LEVEL_HIDDEN = 'HIDDEN';

export const ENTITY_LEVELS = [LEVEL_NORMAL, LEVEL_REPORT_ONLY, LEVEL_HIDDEN];

// Case-insensitive lookup — entity codes in findings payloads may not match the config's casing.
export function entityLevel(code, cfg) {
  if (!code || !cfg) return LEVEL_NORMAL;
  if (cfg[code]) return cfg[code];
  const upper = String(code).toUpperCase();
  if (cfg[upper]) return cfg[upper];
  const hit = Object.keys(cfg).find((k) => k.toUpperCase() === upper);
  return hit ? cfg[hit] : LEVEL_NORMAL;
}

function isHiddenForDisplay(code, cfg) {
  return entityLevel(code, cfg) === LEVEL_HIDDEN;
}

function isExcludedFromExport(code, cfg) {
  const level = entityLevel(code, cfg);
  return level === LEVEL_HIDDEN || level === LEVEL_REPORT_ONLY;
}

// --- Filters over the two common finding shapes in this codebase -------------------------
// 1) an entity-keyed count object: { PERSON: 5, EMAIL: 12, ... }
// 2) an array of finding objects each with an `entity` field.

function filterCountMap(obj, cfg, excludeFn) {
  const out = {};
  Object.keys(obj || {}).forEach((code) => {
    if (!excludeFn(code, cfg)) out[code] = obj[code];
  });
  return out;
}

function filterArray(arr, cfg, excludeFn, entityKey) {
  return (arr || []).filter((item) => {
    const code = item && (item[entityKey] != null ? item[entityKey] : item.entity);
    return !excludeFn(code, cfg);
  });
}

// Drop HIDDEN entities. Accepts either shape; returns the same shape.
export function filterForDisplay(entities, cfg, entityKey = 'entity') {
  if (!cfg || Object.keys(cfg).length === 0) return entities;
  if (Array.isArray(entities)) return filterArray(entities, cfg, isHiddenForDisplay, entityKey);
  if (entities && typeof entities === 'object') {
    return filterCountMap(entities, cfg, isHiddenForDisplay);
  }
  return entities;
}

// Drop HIDDEN and REPORT_ONLY entities. Accepts either shape; returns the same shape.
export function filterForExport(entities, cfg, entityKey = 'entity') {
  if (!cfg || Object.keys(cfg).length === 0) return entities;
  if (Array.isArray(entities)) return filterArray(entities, cfg, isExcludedFromExport, entityKey);
  if (entities && typeof entities === 'object') {
    return filterCountMap(entities, cfg, isExcludedFromExport);
  }
  return entities;
}

// Predicates for callers that render one entity at a time (e.g. a badge loop).
export function shouldDisplayEntity(code, cfg) {
  return !isHiddenForDisplay(code, cfg);
}
export function shouldExportEntity(code, cfg) {
  return !isExcludedFromExport(code, cfg);
}

// --- Discovery "tables" payload filters --------------------------------------------------
// The discovery overview/table payloads share a shape: tables[].fields[].findings = {CODE: n}.
// These strip filtered entity codes out of every field's findings so all downstream transforms
// (overview pie, entity tables, table panel) inherit the filtering from one place. Returns a
// shallow-cloned structure; the input is not mutated.

function filterFindingsObj(findings, cfg, excludeFn) {
  const out = {};
  Object.keys(findings || {}).forEach((code) => {
    if (!excludeFn(code, cfg)) out[code] = findings[code];
  });
  return out;
}

function filterTables(tables, cfg, excludeFn) {
  if (!cfg || Object.keys(cfg).length === 0) return tables;
  if (!Array.isArray(tables)) return tables;
  return tables.map((table) => ({
    ...table,
    fields: (table.fields || []).map((field) => ({
      ...field,
      findings: filterFindingsObj(field.findings, cfg, excludeFn),
    })),
  }));
}

// Strip HIDDEN entities from a discovery `tables` array (overview/landscape views).
export function filterTablesForDisplay(tables, cfg) {
  return filterTables(tables, cfg, isHiddenForDisplay);
}

// Strip a discovery results payload for EXPORT: removes HIDDEN and REPORT_ONLY entities from
// every field's findings. Handles both payload shapes — { tables: [...] } and a bare tables
// array — returning the same shape. Non-table payloads pass through unchanged.
export function filterPayloadForExport(payload, cfg) {
  if (!cfg || Object.keys(cfg).length === 0) return payload;
  if (Array.isArray(payload)) return filterTables(payload, cfg, isExcludedFromExport);
  if (payload && Array.isArray(payload.tables)) {
    return { ...payload, tables: filterTables(payload.tables, cfg, isExcludedFromExport) };
  }
  return payload;
}

// Strip a single table-result `response` ({ table: { fields: [...] } }) for display.
export function filterTableResponseForDisplay(response, cfg) {
  if (!cfg || Object.keys(cfg).length === 0) return response;
  if (!response || !response.table) return response;
  return {
    ...response,
    table: {
      ...response.table,
      fields: (response.table.fields || []).map((field) => ({
        ...field,
        findings: filterFindingsObj(field.findings, cfg, isHiddenForDisplay),
      })),
    },
  };
}
